/**
 * useSphereArchetypes.ts
 *
 * Reads and replaces the set of Archetypes assigned to one sphere. Whether the caller may
 * change it comes from the GET response's `canAssign` — the hook never asks SpiceDB and never
 * re-derives the authority rule. Every write sends the last-read `revision` as `baseRevision`,
 * so a stale edit surfaces as `conflict` with the fresh set already loaded instead of
 * overwriting someone else's change; Undo is simply `save(previousSet)` against the new revision.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { globalSphereCache } from '@center-platform/spheres/cache';
import type { EffectiveExpectedAspect, ExpectedAspectsEvaluation } from 'types/archetype';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import {
  getSphereArchetypes,
  listAvailableArchetypes,
  putSphereArchetypes,
  type ArchetypeAssignment,
  type ArchetypeRowProblem,
  type ArchetypeSelection,
  type AvailableArchetype,
  type SphereArchetypesRead,
} from '../api/sphereArchetypesApi';

export type SphereArchetypesStatus = 'loading' | 'ready' | 'error';
export type AvailableArchetypesStatus = 'idle' | 'loading' | 'ready' | 'error';

export type SaveArchetypesResult = 'saved' | 'conflict' | 'rejected' | 'forbidden' | 'error';

export interface UseSphereArchetypesResult {
  status: SphereArchetypesStatus;
  /** True while a re-read of the sphere already on screen is in flight (`status` stays `ready`). */
  refreshing: boolean;
  archetypes: ArchetypeAssignment[];
  revision: string | null;
  /** The server's answer to "may this caller change the set" — false until the read lands. */
  canAssign: boolean;
  /** Whose authority it is (`self` = only the person this sphere is); undefined until the read lands. */
  assignGate?: 'self' | 'edit';
  /**
   * True when the last read that answered could not determine the authority: `canAssign` is false
   * but is not a denial. Every 200/409 read replaces it; a 403 on save clears it.
   */
  assignGateUnavailable: boolean;
  available: AvailableArchetype[];
  availableStatus: AvailableArchetypesStatus;
  /** Per-row reasons from the last 422; empty otherwise. */
  problems: ArchetypeRowProblem[];
  /** True after the last save met a newer revision; the fresh set is already loaded. */
  conflict: boolean;
  saving: boolean;
  /** Expected aspects from assigned Archetypes; empty until ready. */
  expectedAspects: EffectiveExpectedAspect[];
  /** Evaluation status of expected aspects; undefined until ready. */
  expectedAspectsEvaluation?: ExpectedAspectsEvaluation;
  refetch: () => void;
  loadAvailable: (search?: string) => Promise<void>;
  save: (selection: ArchetypeSelection) => Promise<SaveArchetypesResult>;
  clearFeedback: () => void;
}

const NO_EXPECTED_ASPECTS: EffectiveExpectedAspect[] = [];

export function useSphereArchetypes(
  sphereId: string | undefined,
  options?: { refreshKey?: unknown }
): UseSphereArchetypesResult {
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;
  const [status, setStatus] = useState<SphereArchetypesStatus>('loading');
  const [refreshing, setRefreshing] = useState(false);
  const [read, setRead] = useState<SphereArchetypesRead | null>(null);
  const [available, setAvailable] = useState<AvailableArchetype[]>([]);
  const [availableStatus, setAvailableStatus] = useState<AvailableArchetypesStatus>('idle');
  const [problems, setProblems] = useState<ArchetypeRowProblem[]>([]);
  const [conflict, setConflict] = useState(false);
  const [saving, setSaving] = useState(false);
  /** Only the newest read may commit — a sphere switch must not render the previous sphere's set. */
  const requestIdRef = useRef(0);
  /**
   * The newest read, for `save`. An Undo callback outlives the render that built it, so it must
   * send the revision the previous save produced, not the one it was created under.
   */
  const readRef = useRef<SphereArchetypesRead | null>(null);
  readRef.current = read;

  // Stable identity per read: useExpectedAspectSetup compares consecutive server reads by reference.
  const expectedAspects = useMemo(() => read?.effective?.expectedAspects ?? NO_EXPECTED_ASPECTS, [read]);

  /** The sphere the current read belongs to; a re-read of the same sphere keeps it on screen. */
  const readSphereIdRef = useRef<string | undefined>(undefined);

  const fetchSet = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (!sphereId || !graphClient) {
      readSphereIdRef.current = undefined;
      setRead(null);
      setStatus('loading');
      setRefreshing(false);
      return;
    }
    // A re-read after a save refreshes in place: the field keeps its chips and its Change button
    // rather than flashing back to a skeleton.
    const inPlace = Boolean(readRef.current && readSphereIdRef.current === sphereId);
    setRefreshing(inPlace);
    if (!inPlace) setStatus('loading');
    try {
      const result = await getSphereArchetypes(graphClient, sphereId);
      if (requestIdRef.current !== requestId) return;
      readSphereIdRef.current = sphereId;
      setRead(result);
      setStatus('ready');
    } catch {
      if (requestIdRef.current !== requestId) return;
      readSphereIdRef.current = undefined;
      setRead(null);
      setStatus('error');
    } finally {
      if (requestIdRef.current === requestId) setRefreshing(false);
    }
  }, [sphereId, graphClient]);

  useEffect(() => {
    fetchSet();
  }, [fetchSet, options?.refreshKey]);

  const clearFeedback = useCallback(() => {
    setProblems([]);
    setConflict(false);
  }, []);

  const loadAvailable = useCallback(
    async (search?: string) => {
      if (!sphereId || !graphClient) return;
      setAvailableStatus('loading');
      try {
        setAvailable(await listAvailableArchetypes(graphClient, sphereId, search));
        setAvailableStatus('ready');
      } catch {
        setAvailableStatus('error');
      }
    },
    [sphereId, graphClient]
  );

  const save = useCallback(
    async (selection: ArchetypeSelection): Promise<SaveArchetypesResult> => {
      if (!sphereId || !graphClient) return 'error';
      setSaving(true);
      clearFeedback();
      try {
        const outcome = await putSphereArchetypes(graphClient, sphereId, {
          archetypes: selection.archetypes,
          primary: selection.primary,
          baseRevision: readRef.current?.revision ?? null,
        });
        if (outcome.kind === 'saved' || outcome.kind === 'recorded') {
          // The sphere's cached data carries what the header and pickers read; drop it so the
          // next read sees the new set. A `recorded` failure landed the set but answered without
          // it, so the set is re-read: the user's change was saved, never reported as failed.
          globalSphereCache.clearSphere(sphereId);
          if (outcome.kind === 'saved' && outcome.value) setRead(outcome.value);
          else await fetchSet();
          return 'saved';
        }
        if (outcome.kind === 'conflict') {
          setRead(outcome.value);
          setConflict(true);
          return 'conflict';
        }
        if (outcome.kind === 'rejected') {
          setProblems(outcome.problems);
          return 'rejected';
        }
        // A 403 is the server's answer, so whatever authority outage the last read reported is over.
        setRead((prev) => (prev ? { ...prev, canAssign: false, assignGateUnavailable: false } : prev));
        return 'forbidden';
      } catch {
        return 'error';
      } finally {
        setSaving(false);
      }
    },
    [sphereId, graphClient, clearFeedback, fetchSet]
  );

  return {
    status,
    refreshing,
    archetypes: read?.archetypes ?? [],
    revision: read?.revision ?? null,
    canAssign: read?.canAssign === true,
    assignGate: read?.assignGate,
    assignGateUnavailable: read?.assignGateUnavailable === true,
    available,
    availableStatus,
    problems,
    conflict,
    saving,
    expectedAspects,
    expectedAspectsEvaluation: read?.effective?.expectedAspectsEvaluation,
    refetch: fetchSet,
    loadAvailable,
    save,
    clearFeedback,
  };
}
